<template>
  <v-form ref="form">
    <v-container class="pa-7 d-flex flex-column align-start" fluid>
      <h2>Password PCare</h2>
      <div
        class="chip-message"
        :style="{
          backgroundColor: chip.color,
          borderRadius: '5px',
          display: 'flex',
          alignItems: 'flex-start',
          padding: '4px 8px',
          whiteSpace: 'pre-line',
          textAlign: 'left',
        }"
      >
        <v-icon :color="chip.iconColor" class="mr-2">{{ chip.icon }}</v-icon>

        <div>
          <strong class="font-weight-bold" v-show="bpjsError">
            BPJS Error atau Tidak Terhubung.
          </strong>
          <div>{{ chip.text }}</div>
        </div>
      </div>
      <label class="required mb-n4">
        Username
      </label>
      <v-text-field
        v-model="username"
        @keydown="onEnter"
        :rules="rules"
        readonly
        dense
        light
      />
      <label class="required mb-n4">
        Password
      </label>
      <v-text-field
        :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
        :type="showPassword ? 'text' : 'password'"
        @click:append="showPassword = !showPassword"
        v-model="password"
        @keydown="onEnter"
        @input="validationPass"
        :rules="rules"
        :readonly="!isEditing"
        dense
        light
      />
      <div class="button-container">
        <v-btn
          color="error"
          class="mr-3 text-capitalize"
          v-if="isEditing"
          @click="edit()"
          outlined
          dense
          depressed
          light
          >Batal</v-btn
        >
        <v-btn
          @click="isEditing ? update() : edit()"
          color="primary"
          class="text-capitalize"
          :loading="loading.update"
          :disabled="saveDisable"
          dense
          depressed
          light
          >{{ isEditing ? 'Simpan Perubahan' : 'Ubah' }}</v-btn
        >
      </div>
    </v-container>
    <v-snackbar
      timeout="4000"
      content-class="d-flex justify-space-between"
      :color="snackbar.color"
      v-model="snackbar.show"
    >
      {{ snackbar.text }}
      <v-icon small style="cursor: pointer" @click="snackbar.show = false"
        >mdi-close</v-icon
      >
    </v-snackbar>
  </v-form>
</template>

<script>
import {
  AuthenticationCheck,
  getConfigurationData,
  putUpdatePcareData,
} from '@/fetchApi/Setting/PcarePassword';
import Swal from 'sweetalert2';

export default {
  name: 'PcarePassword',
  data() {
    return {
      saveDisable: false,
      bpjsError: false,
      chip: {
        icon: '',
        color: '',
        text: '',
        iconColor: '',
      },
      rules: [v => !!v || 'Wajib diisi'],
      snackbar: {
        show: false,
        text: 'Password bridging berhasil diubah',
        color: '#31b057',
      },
      loading: {
        update: false,
      },
      firstChip: {
        icon: '',
        color: '',
        text: '',
        iconColor: '',
      },
      username: '',
      password: '',
      showPassword: false,
      isEditing: false,
    };
  },
  mounted() {
    this.pcareAuthCheck();
    this.getConfiguration();
  },
  methods: {
    validationPass() {
      if (this.password.includes(':')) {
        this.chip = {
          icon: 'mdi-alert',
          color: '#fff3f3',
          text:
            'Password PCare tidak valid karena mengandung tanda titik dua (:).\nGunakan kombinasi huruf dan angka, ubah melalui aplikasi PCare, lalu masukkan ulang di Neurovi.',
          iconColor: 'error',
        };
        this.saveDisable = true;
      } else {
        this.chip = this.firstChip;
        this.saveDisable = false;
      }
    },
    async pcareAuthCheck() {
      try {
        this.bpjsError = false;
        const response = await AuthenticationCheck();
        this.chip = {
          icon: 'mdi-check-circle',
          color: '#e5fff0',
          text: response.data.pesan,
          iconColor: 'success',
        };
      } catch (error) {
        if (error?.response?.data?.code === 401) {
          this.chip = {
            icon: 'mdi-close-circle',
            color: '#fff3f3',
            text: error?.response?.data?.pesan,
            iconColor: 'error',
          };
          return;
        } else if (error?.response?.status >= 500) {
          this.bpjsError = true;
          this.chip = {
            icon: 'mdi-alert',
            color: '#fff3f3',
            text:
              'Anda masih bisa mengubah password dan akan terautentikasi jika BPJS terhubung kembali',
            iconColor: 'error',
          };
          return;
        }
        if (error?.response?.status >= 500) {
          Swal.fire(
            'Error',
            'Terjadi kesalahan, terdapat error pada bridging BPJS',
            'error',
          );
          return;
        }
        Swal.fire(
          'Error',
          'Terjadi kesalahan saat memeriksa autentikasi Pcare',
          'error',
        );
      } finally {
        this.setFirstChip();
      }
    },
    setFirstChip() {
      this.firstChip = this.chip;
    },
    async getConfiguration() {
      try {
        const { username, password } = await getConfigurationData();
        this.username = username;
        this.password = password;
      } catch {
        Swal.fire(
          'Error',
          'Terjadi kesalahan saat mendapatkan data username dan password',
          'error',
        );
      }
    },
    async update() {
      try {
        this.loading.update = true;
        if (!this.$refs.form.validate()) return;
        await putUpdatePcareData({ password: this.password });
        this.snackbar = {
          show: true,
          text: 'Password bridging berhasil diubah',
          color: '#31b057',
        };
      } catch (error) {
        if (error.response?.status === 400) {
          this.snackbar = {
            show: true,
            text: error.response?.data?.pesan,
            color: 'error',
          };
          return;
        }
        if (error?.response?.status >= 500) {
          Swal.fire(
            'Error',
            'Terjadi kesalahan, terdapat error pada bridging BPJS',
            'error',
          );
          return;
        }
        this.snackbar = {
          show: true,
          text: 'Terjadi kesalahan, gagal mengubah password PCare',
          color: 'error',
        };
      } finally {
        this.loading.update = false;
        this.isEditing = false;
        this.pcareAuthCheck();
      }
    },
    onEnter(e) {
      if (e.keyCode === 13) {
        this.update();
        e.preventDefault();
      }
    },
    edit() {
      this.isEditing = !this.isEditing;
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  gap: 1vw;
  .chip-message {
    font-weight: 400;
    border: 1px solid #bdbdbd !important;
    border-radius: 5px;
    font-size: 14px;

    i {
      font-size: 1vw;
    }
  }

  label {
    font-size: 0.9vw;
    color: #3f484a;
    font-weight: 500;
  }

  .v-input {
    width: 30vw;
  }

  .button-container {
    width: 30vw;
    display: flex;
    justify-content: flex-end;

    button {
      //   padding: 0 !important;
      height: 1.4vw !important;
      letter-spacing: 0.0001px;
    }
  }
  .required:after {
    content: ' *';
    color: red;
  }
}
</style>
